/**
 * PII Sanitization Utilities
 * Remove or mask personally identifiable information from analytics data
 */

/** Default PII fields to sanitize */
const DEFAULT_PII_FIELDS = [
  'email',
  'phone',
  'telephone',
  'mobile',
  'address',
  'street',
  'city',
  'zipcode',
  'zip',
  'postalCode',
  'postal_code',
  'name',
  'firstName',
  'first_name',
  'lastName',
  'last_name',
  'fullName',
  'full_name',
  'password',
  'secret',
  'token',
  'apiKey',
  'api_key',
  'ssn',
  'socialSecurity',
  'social_security',
  'creditCard',
  'credit_card',
  'cardNumber',
  'card_number',
  'cvv',
  'nationalId',
  'national_id',
  'passport',
  'driverLicense',
  'driver_license',
]

/** Email regex pattern */
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g

/** Phone regex pattern (various formats) */
const PHONE_REGEX = /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g

/** Credit card regex pattern */
const CREDIT_CARD_REGEX = /\b(?:\d{4}[-\s]?){3}\d{4}\b/g

/** SSN regex pattern */
const SSN_REGEX = /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g

/**
 * Check if a field name matches any PII field
 */
function isPiiField(fieldName: string, piiFields: string[]): boolean {
  const lowerFieldName = fieldName.toLowerCase()
  return piiFields.some((pii) => lowerFieldName.includes(pii.toLowerCase()))
}

/**
 * Mask a string value
 */
function maskValue(value: string): string {
  if (value.length <= 4) {
    return '***'
  }
  return value.substring(0, 2) + '***' + value.substring(value.length - 2)
}

/**
 * Sanitize a string by removing PII patterns
 */
function sanitizeString(value: string): string {
  return value
    .replace(EMAIL_REGEX, '[EMAIL_REDACTED]')
    .replace(PHONE_REGEX, '[PHONE_REDACTED]')
    .replace(CREDIT_CARD_REGEX, '[CARD_REDACTED]')
    .replace(SSN_REGEX, '[SSN_REDACTED]')
}

/**
 * Sanitize a URL by removing query params that might contain PII
 */
export function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url)
    const piiParams = ['email', 'token', 'password', 'key', 'secret', 'auth']

    piiParams.forEach((param) => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]')
      }
    })

    return urlObj.toString()
  } catch {
    // If URL parsing fails, just sanitize as string
    return sanitizeString(url)
  }
}

/**
 * Deep sanitize an object, removing or masking PII fields
 */
export function sanitizePii<T>(
  data: T,
  customPiiFields?: string[]
): T {
  if (data === null || data === undefined) {
    return data
  }

  const piiFields = customPiiFields
    ? [...DEFAULT_PII_FIELDS, ...customPiiFields]
    : DEFAULT_PII_FIELDS

  // Handle primitives
  if (typeof data === 'string') {
    return sanitizeString(data) as T
  }

  if (typeof data !== 'object') {
    return data
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizePii(item, customPiiFields)) as T
  }

  // Handle objects
  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (isPiiField(key, piiFields)) {
      // Mask PII fields
      if (typeof value === 'string') {
        sanitized[key] = maskValue(value)
      } else {
        sanitized[key] = '[REDACTED]'
      }
    } else if (typeof value === 'string') {
      // Sanitize string values
      sanitized[key] = sanitizeString(value)
    } else if (typeof value === 'object' && value !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizePii(value, customPiiFields)
    } else {
      sanitized[key] = value
    }
  }

  return sanitized as T
}

/**
 * Hash a string (for email hashing etc)
 * Uses simple hash for client-side, not cryptographic
 */
export function hashString(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16)
}

/**
 * Hash an email address for analytics
 */
export function hashEmail(email: string): string {
  return hashString(email.toLowerCase().trim())
}
